import { OpenAPI } from '../../../openapi/core/OpenAPI'
import { request as openApiRequest } from '../../../openapi/core/request'
import type { BrandKit } from '../types'

/** GET /v1/businesses/brand-kit - requires business context. */
export function fetchBrandKit(): Promise<BrandKit> {
  return openApiRequest(OpenAPI, {
    method: 'GET',
    url: '/v1/businesses/brand-kit',
  })
}

/** PATCH /v1/businesses/brand-kit - partial update for field-level save. */
export function patchBrandKit(partial: Partial<BrandKit>): Promise<BrandKit> {
  return openApiRequest(OpenAPI, {
    method: 'PATCH',
    url: '/v1/businesses/brand-kit',
    body: partial,
    mediaType: 'application/json',
  })
}

// ─── Website Scan Types ────────────────────────────────────────────────────────
// The wire types live in `@mondaygpt/types` (server emits them via SSE) — we
// re-export here so existing import sites keep working without churn.

import type { ScanStreamEvent, ScanStreamInput } from '@mondaygpt/types'
export type {
  ScanStreamEvent,
  ScanStreamInput,
  WebsiteScanResult,
  ScanScreenshotPayload,
  ScanInstagramProfilePayload,
  ScanInstagramPostPreview,
  WebsiteScanColors,
  WebsiteScanSection,
} from '@mondaygpt/types'

/** POST /v1/businesses/check-url — lightweight HEAD-request reachability check. */
export function checkUrl(
  websiteUrl: string
): Promise<{ reachable: boolean; statusCode: number | null }> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/businesses/check-url',
    body: { websiteUrl },
    mediaType: 'application/json',
  })
}

export interface FindSocialsResponse {
  instagram: string | null
  facebook: string | null
  twitter: string | null
  linkedin: string | null
  tiktok: string | null
  pinterest: string | null
}

/**
 * POST /v1/businesses/find-socials — fetch a website's HTML and pull the social
 * handles it links to. Used by the onboarding Presence step to cross-verify
 * what the user typed against what the site itself declares (since the
 * platforms hide real-vs-fake from unauthenticated checks).
 */
export function findSocialsOnWebsite(websiteUrl: string): Promise<FindSocialsResponse> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/businesses/find-socials',
    body: { websiteUrl },
    mediaType: 'application/json',
  })
}

/** POST /v1/businesses/download-logo — download an external image and persist as an attachment. */
export function downloadLogo(
  logoUrl: string
): Promise<{ attachmentId: string; publicUrl: string }> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/businesses/download-logo',
    body: { logoUrl },
    mediaType: 'application/json',
  })
}

/** POST /v1/businesses/check-facebook-page — check if a handle points to a valid Facebook Page. */
export function checkFacebookPage(
  handle: string
): Promise<{ exists: boolean; isPage: boolean; pageName?: string }> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/businesses/check-facebook-page',
    body: { handle },
    mediaType: 'application/json',
  })
}

// ─── Social Analysis Types ────────────────────────────────────────────────────

export interface AnalyzeSocialParams {
  instagramHandle?: string
  facebookHandle?: string
  facebookPageId?: string
  websiteUrl?: string
}

/** POST /v1/businesses/check-instagram-handle — verify if handle is a Business/Creator account. */
export function checkInstagramHandle(
  handle: string
): Promise<{ valid: boolean; accountType?: string; name?: string }> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/businesses/check-instagram-handle',
    body: { handle },
    mediaType: 'application/json',
  })
}

/**
 * POST /v1/businesses/scan as an SSE stream.
 *
 * Returns an async iterator of typed events plus an `abort()` to cancel mid-flight.
 */
export function streamWebsiteScan(input: ScanStreamInput): {
  events: AsyncIterable<ScanStreamEvent>
  abort: () => void
} {
  const ac = new AbortController()
  async function* iterate(): AsyncGenerator<ScanStreamEvent> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    }
    if (OpenAPI.TOKEN) {
      const token = await resolveToken()
      // An empty `Bearer ` header trips some proxies into 401 before the
      // request reaches the server; only set the header when we have a token.
      if (token) headers.Authorization = `Bearer ${token}`
    }

    const res = await fetch(`${OpenAPI.BASE}/v1/businesses/scan`, {
      method: 'POST',
      headers,
      body: JSON.stringify(input),
      signal: ac.signal,
    })
    if (!res.ok || !res.body) {
      throw new Error(`scan failed: ${res.status}`)
    }
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) return
        buffer += decoder.decode(value, { stream: true })
        let sep = buffer.indexOf('\n\n')
        while (sep !== -1) {
          const raw = buffer.slice(0, sep)
          buffer = buffer.slice(sep + 2)
          const event = parseSseFrame(raw)
          if (event) yield event
          sep = buffer.indexOf('\n\n')
        }
      }
    } finally {
      // Fire-and-forget cancel — awaiting can hang on stuck responses and
      // delay generator teardown. releaseLock still runs synchronously.
      reader.cancel().catch(() => {})
      reader.releaseLock()
    }
  }
  return { events: iterate(), abort: () => ac.abort() }
}

function parseSseFrame(raw: string): ScanStreamEvent | null {
  if (raw.startsWith(':')) return null
  let dataLine = ''
  for (const line of raw.split('\n')) {
    if (line.startsWith('data:')) dataLine += line.slice(5).trim()
  }
  if (!dataLine) return null
  try {
    return JSON.parse(dataLine) as ScanStreamEvent
  } catch (err) {
    // Surfaces to error tracking via the console bridge so chunking weirdness
    // from proxies / unserializable payloads doesn't silently leave the UI hung.
    console.warn('[scan stream] dropped malformed SSE frame:', dataLine.slice(0, 200), err)
    return null
  }
}

async function resolveToken(): Promise<string> {
  const t = OpenAPI.TOKEN
  if (typeof t === 'function') return (await (t as () => Promise<string>)()) ?? ''
  return (t as string) ?? ''
}
